// client/src/coms/components/StickerPackCard.js
// One sticker pack in Settings → Stickers or Server settings → Stickers: header (icon, name, creator, count,
// access), the stickers (click = edit keywords/name, 🔍 = full size, ✕ = delete after confirmation,
// drag to reorder), and the actions the server allows (can_edit / is_mine / can_export come from the server).
import React, { useEffect, useState } from 'react';
import { comsApiDelete, comsApiGet, comsApiPatch, comsApiPost, comsApiPut } from '../comsApi';
import { isProbablyAnimated, prepareImageFile, uploadMultipart } from '../imagePrep';
import StickerEditorModal, { ConfirmDialog, ModalShell, StickerPreview } from './StickerEditorModal';
import StickerUploader from './StickerUploader';
import { PackIcon, VISIBILITY_LABEL, formatPrice } from './PackInfoDialog';
import { PublishDialog, StatsDialog, copyPackToDevice } from './PackMarketplaceDialogs';

const field = { background: 'var(--vxs-1, #111214)', color: 'var(--vx-text, #fff)', border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))', borderRadius: 8, padding: '8px 10px', fontSize: 13 };

function errText(e, fallback) {
  return String((e && e.data && e.data.error) || (e && e.message) || fallback);
}

export const ACCESS_LABEL = {
  owner: 'Created by you',
  installed: 'Installed',
  shared: 'Shared with you',
  purchased: 'Purchased',
  server: 'From this server',
};

async function exportPack(pack) {
  const res = await fetch(`/api/coms/stickers/packs/${pack.id}/export`, { credentials: 'include' });
  const data = res.ok ? null : await res.json().catch(() => null);
  if (!res.ok) throw new Error((data && data.error) || 'Export failed');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(await res.blob());
  a.download = `${pack.name.replace(/[^A-Za-z0-9_-]+/g, '_') || 'stickers'}.voxstickers.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

// Name, description, visibility and icon (creator, or server-pack managers for name/description/icon).
function PackSettingsDialog({ pack, onClose, onSaved }) {
  const [name, setName] = useState(pack.name);
  const [description, setDescription] = useState(pack.description || '');
  const [visibility, setVisibility] = useState(pack.visibility);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function save() {
    setBusy(true);
    setErr('');
    try {
      const body = { name, description };
      if (pack.is_mine && pack.visibility !== 'server') body.visibility = visibility;
      await comsApiPatch(`/stickers/packs/${pack.id}`, body);
      onSaved();
    } catch (e) {
      setErr(errText(e, 'Could not save the pack.'));
      setBusy(false);
    }
  }

  async function setIcon(file) {
    if (!file) return;
    setBusy(true);
    setErr('');
    try {
      const prepared = isProbablyAnimated(file) ? file : await prepareImageFile(file, { size: 256, mode: 'crop' });
      await uploadMultipart(`/stickers/packs/${pack.id}/icon`, {}, prepared);
      onSaved({ keepOpen: true });
    } catch (e) {
      setErr(errText(e, 'Could not set the icon.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell label={`Pack details ${pack.name}`} onClose={() => !busy && onClose()} width={500}>
      <div style={{ fontSize: 17, fontWeight: 900 }}>Pack details</div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
        <PackIcon pack={pack} size={56} />
        <label className="coms-mini-btn" style={{ cursor: 'pointer' }}>
          Change icon…
          <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" aria-label="Pack icon" style={{ display: 'none' }} onChange={(e) => setIcon(e.target.files && e.target.files[0])} />
        </label>
      </div>
      <label style={{ display: 'block', marginTop: 12, fontSize: 12, fontWeight: 800 }}>
        Name
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} style={{ ...field, width: '100%', boxSizing: 'border-box', marginTop: 4 }} />
      </label>
      <label style={{ display: 'block', marginTop: 10, fontSize: 12, fontWeight: 800 }}>
        Description
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} rows={3} style={{ ...field, width: '100%', boxSizing: 'border-box', marginTop: 4, resize: 'vertical', fontFamily: 'inherit' }} />
      </label>
      {pack.is_mine && pack.visibility !== 'server' ? (
        <label style={{ display: 'block', marginTop: 10, fontSize: 12, fontWeight: 800 }}>
          Who can use it
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} style={{ ...field, width: '100%', marginTop: 4 }}>
            <option value="private">Private: only me and accounts I share it with</option>
            <option value="friends">Friends: my friends can add it with the share code</option>
            <option value="unlisted">Anyone with the share code (not listed in the Marketplace)</option>
            <option value="public">Published: listed in the Marketplace</option>
          </select>
        </label>
      ) : null}
      {pack.is_mine && (pack.visibility === 'public' || pack.visibility === 'unlisted') && (visibility === 'private' || visibility === 'friends') ? (
        <div style={{ fontSize: 12, marginTop: 8, color: '#fcd34d' }}>Servers that enabled this pack will lose it, and people who added it for free can no longer use it unless you share it with them. Buyers keep it. To only stop listing it, choose “Anyone with the share code”.</div>
      ) : null}
      {err ? <div className="coms-form-error" role="alert" style={{ marginTop: 10 }}>{err}</div> : null}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" className="coms-mini-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="coms-mini-btn primary" onClick={save} disabled={busy || name.trim().length < 2}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </ModalShell>
  );
}

// Share code (copy / replace) and specific accounts (creator only).
function PackShareDialog({ pack, onClose, onChanged }) {
  const [code, setCode] = useState(pack.share_code || '');
  const [grants, setGrants] = useState(null);
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');

  const loadGrants = () => comsApiGet(`/stickers/packs/${pack.id}/grants`).then((r) => setGrants((r && r.grants) || [])).catch(() => setGrants([]));
  useEffect(() => {
    loadGrants();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function run(fn, fallback) {
    setBusy(true);
    setErr('');
    setNote('');
    try {
      await fn();
    } catch (e) {
      setErr(errText(e, fallback));
    } finally {
      setBusy(false);
    }
  }

  const explain =
    pack.visibility === 'public'
      ? 'Anyone with the code (or in the Marketplace) can view and add this pack.'
      : pack.visibility === 'friends'
        ? 'Your friends can add this pack with the code. Other people need to be added below.'
        : 'Private: the code only works for accounts you add below. Everyone else sees nothing.';

  return (
    <ModalShell label={`Share ${pack.name}`} onClose={() => !busy && onClose()} width={480}>
      <div style={{ fontSize: 17, fontWeight: 900 }}>Share “{pack.name}”</div>
      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6, lineHeight: 1.45 }}>{explain}</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
        <code style={{ ...field, fontSize: 15, letterSpacing: 1, userSelect: 'all' }} aria-label="Share code">{code}</code>
        <button type="button" className="coms-mini-btn" onClick={() => navigator.clipboard && navigator.clipboard.writeText(code).then(() => setNote('Copied.')).catch(() => {})}>Copy</button>
        <button type="button" className="coms-mini-btn" disabled={busy} title="The old code stops working" onClick={() => run(async () => { const r = await comsApiPost(`/stickers/packs/${pack.id}/share-code`, {}); setCode(r.share_code); setNote('New code created; the old one no longer works.'); onChanged(); }, 'Could not create a new code.')}>
          New code
        </button>
      </div>
      <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>Pack ID {pack.id}</div>

      {pack.visibility !== 'public' ? (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800 }}>People with access</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" aria-label="Username to share with" style={{ ...field, flex: 1 }}
              onKeyDown={(e) => e.key === 'Enter' && username.trim() && run(async () => { await comsApiPost(`/stickers/packs/${pack.id}/grants`, { username: username.trim() }); setUsername(''); await loadGrants(); }, 'Could not share.')} />
            <button type="button" className="coms-mini-btn primary" disabled={busy || !username.trim()} onClick={() => run(async () => { await comsApiPost(`/stickers/packs/${pack.id}/grants`, { username: username.trim() }); setUsername(''); await loadGrants(); }, 'Could not share.')}>
              Add
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
            {grants === null ? <div style={{ fontSize: 12, opacity: 0.6 }}>Loading…</div> : null}
            {grants && !grants.length ? <div style={{ fontSize: 12, opacity: 0.6 }}>Nobody yet.</div> : null}
            {(grants || []).map((g) => (
              <div key={g.user_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <span style={{ flex: 1 }}>{g.username}{g.source === 'purchase' ? ' · purchased' : ''}</span>
                {g.source !== 'purchase' ? (
                  <button type="button" className="coms-mini-btn tiny" disabled={busy} onClick={() => run(async () => { await comsApiDelete(`/stickers/packs/${pack.id}/grants/${g.user_id}`); await loadGrants(); }, 'Could not remove access.')}>
                    Remove
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {err ? <div className="coms-form-error" role="alert" style={{ marginTop: 10 }}>{err}</div> : null}
      {note ? <div role="status" style={{ fontSize: 12, marginTop: 10, color: '#a7f3d0' }}>{note}</div> : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" className="coms-mini-btn" onClick={onClose} disabled={busy}>Done</button>
      </div>
    </ModalShell>
  );
}

// pack: from GET /stickers/packs (full sticker list). onChanged(): reload the list. extraActions: node.
export default function StickerPackCard({ pack, onChanged, onError, extraActions = null }) {
  const [dialog, setDialog] = useState(null); // { kind, stickerId? }
  const [uploading, setUploading] = useState(false);
  const [dragId, setDragId] = useState(0);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const stickers = pack.stickers || [];
  const listable = pack.is_mine && pack.visibility !== 'server';
  const canEdit = !!pack.can_edit;
  const find = (id) => stickers.find((s) => s.id === id);

  async function act(fn, fallback) {
    setBusy(true);
    try {
      await fn();
      await onChanged();
    } catch (e) {
      if (typeof onError === 'function') onError(errText(e, fallback));
    } finally {
      setBusy(false);
    }
  }

  const saveOrder = (ids) => act(() => comsApiPut(`/stickers/packs/${pack.id}/order`, { sticker_ids: ids }), 'Could not reorder.');

  function move(id, dir) {
    const ids = stickers.map((s) => s.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    saveOrder(ids);
  }

  function dropOn(targetId) {
    if (!dragId || dragId === targetId) return;
    const ids = stickers.map((s) => s.id).filter((x) => x !== dragId);
    ids.splice(ids.indexOf(targetId), 0, dragId);
    setDragId(0);
    saveOrder(ids);
  }

  const editing = dialog && dialog.kind === 'edit' ? find(dialog.stickerId) : null;
  const previewing = dialog && dialog.kind === 'preview' ? find(dialog.stickerId) : null;
  const deleting = dialog && dialog.kind === 'delete' ? find(dialog.stickerId) : null;
  const index = editing ? stickers.indexOf(editing) : -1;
  const accessLabel = pack.is_mine ? (pack.visibility === 'server' ? `Created by you for ${pack.server_name || 'a server'}` : ACCESS_LABEL.owner) : ACCESS_LABEL[pack.access] || '';

  return (
    <div className="vox-pack-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <PackIcon pack={pack} size={44} />
        <div style={{ minWidth: 0, flex: '1 1 180px' }}>
          <div style={{ fontWeight: 800, overflowWrap: 'anywhere' }}>{pack.name}</div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            {pack.is_mine ? 'by you' : `by ${pack.creator || 'someone'}`} · {pack.sticker_count}/60 · ID {pack.id}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
            <span className="vox-pack-badge">{VISIBILITY_LABEL[pack.visibility] || pack.visibility}</span>
            {accessLabel ? <span className="vox-pack-badge">{accessLabel}</span> : null}
            {!pack.is_free ? <span className="vox-pack-badge paid">{formatPrice(pack.price)}</span> : null}
            {pack.origin === 'import' && pack.is_mine ? <span className="vox-pack-badge">Imported</span> : null}
            {pack.is_mine ? <span className="vox-pack-badge server" title="Stored online, shareable and Marketplace ready">☁️ Voxemera Server</span> : null}
            {listable && pack.marketplace_status === 'published' ? <span className="vox-pack-badge free">In the Marketplace</span> : null}
            {listable && pack.marketplace_status === 'unpublished' ? <span className="vox-pack-badge">Unpublished</span> : null}
            {pack.install_count ? <span className="vox-pack-badge" title="People who have this pack">👥 {pack.install_count}</span> : null}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {canEdit ? (
            <>
              <button type="button" className="coms-mini-btn tiny primary" onClick={() => setUploading((v) => !v)} aria-expanded={uploading}>
                Add stickers
              </button>
              <button type="button" className="coms-mini-btn tiny" onClick={() => setDialog({ kind: 'settings' })}>Details</button>
            </>
          ) : null}
          {pack.is_mine && pack.visibility !== 'server' ? (
            <button type="button" className="coms-mini-btn tiny" onClick={() => setDialog({ kind: 'share' })}>Share</button>
          ) : null}
          {listable ? (
            pack.marketplace_status === 'published' ? (
              <>
                <button type="button" className="coms-mini-btn tiny" onClick={() => setDialog({ kind: 'price' })}>Price</button>
                <button type="button" className="coms-mini-btn tiny" disabled={busy} onClick={() => act(() => comsApiPost(`/stickers/packs/${pack.id}/unpublish`, {}), 'Could not unpublish.')}>Unpublish</button>
              </>
            ) : (
              <button type="button" className="coms-mini-btn tiny" disabled={!stickers.length} title={stickers.length ? 'List this pack in the Marketplace' : 'Add stickers first'} onClick={() => setDialog({ kind: 'publish' })} data-testid={`publish-${pack.id}`}>
                Publish…
              </button>
            )
          ) : null}
          {pack.can_edit ? <button type="button" className="coms-mini-btn tiny" onClick={() => setDialog({ kind: 'stats' })}>Stats</button> : null}
          {pack.is_mine && stickers.length ? (
            <button
              type="button"
              className="coms-mini-btn tiny"
              disabled={busy}
              title="Keep a local copy on this device. The server pack is not changed."
              onClick={() =>
                act(async () => {
                  await copyPackToDevice(pack);
                  setNote(`A local copy of “${pack.name}” is now on this device (My packs).`);
                }, 'Could not copy the pack to this device.')
              }
            >
              Copy to this device
            </button>
          ) : null}
          {pack.can_export ? (
            <button type="button" className="coms-mini-btn tiny" onClick={() => exportPack(pack).catch((e) => onError && onError(errText(e, 'Export failed.')))}>Export</button>
          ) : null}
          {extraActions}
          {canEdit ? (
            <button type="button" className="coms-mini-btn tiny vox-sbm-danger-outline" onClick={() => setDialog({ kind: 'delete-pack' })}>Delete pack</button>
          ) : pack.installed ? (
            <button type="button" className="coms-mini-btn tiny" disabled={busy} onClick={() => act(() => comsApiDelete(`/stickers/installs/${pack.id}`), 'Remove failed.')}>Remove</button>
          ) : null}
        </div>
      </div>
      {pack.description ? <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8, overflowWrap: 'anywhere' }}>{pack.description}</div> : null}
      {note ? <div role="status" style={{ fontSize: 12, marginTop: 8, color: '#a7f3d0' }}>{note}</div> : null}

      {uploading && canEdit ? (
        <div style={{ marginTop: 12 }}>
          <StickerUploader pack={pack} roomLeft={Math.max(0, 60 - (pack.sticker_count || 0))} onClose={() => setUploading(false)} onUploaded={onChanged} />
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
        {!stickers.length ? <div style={{ fontSize: 12, opacity: 0.6 }}>{canEdit ? 'No stickers yet. Use “Add stickers”.' : 'No stickers.'}</div> : null}
        {stickers.map((s) => {
          const kw = s.keywords || [];
          return (
            <div
              key={s.id}
              style={{ width: 92, display: 'flex', flexDirection: 'column', gap: 4, opacity: dragId === s.id ? 0.5 : 1 }}
              draggable={canEdit}
              onDragStart={(e) => {
                setDragId(s.id);
                try {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', String(s.id));
                } catch {}
              }}
              onDragEnd={() => setDragId(0)}
              onDragOver={(e) => canEdit && dragId && e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                dropOn(s.id);
              }}
            >
              <div style={{ position: 'relative', width: 92, height: 92 }}>
                <button type="button" className="vox-sticker-tile" aria-label={`${canEdit ? 'Edit' : 'Show keywords for'} ${s.name}`} title={canEdit ? `Edit ${s.name} (drag to reorder)` : s.name} onClick={() => setDialog({ kind: 'edit', stickerId: s.id })}>
                  <img src={s.url} alt="" loading="lazy" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
                </button>
                <button type="button" className="vox-sticker-tile-btn" aria-label={`View ${s.name} full size`} title="View full size" style={{ left: 3 }} onClick={(e) => { e.stopPropagation(); setDialog({ kind: 'preview', stickerId: s.id }); }}>
                  🔍
                </button>
                {canEdit ? (
                  <button type="button" className="vox-sticker-tile-btn" aria-label={`Delete ${s.name}`} title="Delete sticker" style={{ right: 3 }} onClick={(e) => { e.stopPropagation(); setDialog({ kind: 'delete', stickerId: s.id }); }}>
                    ✕
                  </button>
                ) : null}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.name}>{s.name}</div>
              {kw.length ? (
                <div className="vox-sticker-keywords" title={kw.join(', ')}>{kw.join(' · ')}</div>
              ) : canEdit ? (
                <button type="button" className="vox-sticker-add-kw" onClick={() => setDialog({ kind: 'edit', stickerId: s.id })}>+ Add keywords</button>
              ) : (
                <div className="vox-sticker-keywords" style={{ opacity: 0.4 }}>No keywords</div>
              )}
            </div>
          );
        })}
      </div>

      {editing ? (
        <StickerEditorModal
          key={editing.id}
          sticker={editing}
          pack={pack}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await onChanged();
          }}
          onPreview={() => setDialog({ kind: 'preview', stickerId: editing.id, back: editing.id })}
          onMove={(dir) => move(editing.id, dir)}
          canMoveEarlier={index > 0}
          canMoveLater={index >= 0 && index < stickers.length - 1}
        />
      ) : null}
      {previewing ? <StickerPreview sticker={previewing} onClose={() => setDialog(dialog.back ? { kind: 'edit', stickerId: dialog.back } : null)} /> : null}
      {deleting ? (
        <ConfirmDialog
          title="Delete sticker?"
          message={`“${deleting.name}” will be removed from ${pack.name}${(deleting.keywords || []).length ? ', along with its keywords' : ''}. Messages that already contain it keep showing it.`}
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={async () => {
            await act(() => comsApiDelete(`/stickers/stickers/${deleting.id}`), 'Delete failed.');
            setDialog(null);
          }}
        />
      ) : null}
      {dialog && dialog.kind === 'delete-pack' ? (
        <ConfirmDialog
          title="Delete sticker pack?"
          message={`“${pack.name}” and its ${pack.sticker_count} sticker${pack.sticker_count === 1 ? '' : 's'} will be removed from Voxemera Server for everyone who uses it, including servers that enabled it. Stickers already sent in messages keep showing. Packs that people bought cannot be deleted; unpublish them instead.`}
          confirmLabel="Delete pack"
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={async () => {
            await act(() => comsApiDelete(`/stickers/packs/${pack.id}`), 'Delete failed.');
            setDialog(null);
          }}
        />
      ) : null}
      {dialog && dialog.kind === 'settings' ? (
        <PackSettingsDialog pack={pack} onClose={() => setDialog(null)} onSaved={async (o) => { if (!(o && o.keepOpen)) setDialog(null); await onChanged(); }} />
      ) : null}
      {dialog && dialog.kind === 'share' ? <PackShareDialog pack={pack} onClose={() => setDialog(null)} onChanged={onChanged} /> : null}
      {dialog && (dialog.kind === 'publish' || dialog.kind === 'price') ? (
        <PublishDialog pack={pack} mode={dialog.kind} onClose={() => setDialog(null)} onDone={async () => { setDialog(null); await onChanged(); }} />
      ) : null}
      {dialog && dialog.kind === 'stats' ? <StatsDialog pack={pack} onClose={() => setDialog(null)} /> : null}
    </div>
  );
}
